import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { formatINR } from '../utils/currency';
import {
  ArrowLeft, Plane, MapPin, Calendar, Plus, ChevronDown,
  FileText, MessageSquare, Send, AlertTriangle, Download, Globe, Home
} from 'lucide-react';
import { ExportButton } from '../components/ExportButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtCur = (amount, currency) => {
  const val = Number(amount || 0);
  if (currency === 'USD') return `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (currency === 'EUR') return `€${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (currency === 'GBP') return `£${val.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return formatINR(val);
};

const STATUS_STYLES = {
  ongoing: 'bg-blue-50 text-blue-700 border-blue-200',
  submitted: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  settled: 'bg-purple-50 text-purple-700 border-purple-200',
};
const EXPENSE_STATUS_STYLES = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
};

const TripDetail = () => {
  const { tripId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [statusRemarks, setStatusRemarks] = useState('');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteLoading, setNoteLoading] = useState(false);

  useEffect(() => { fetchTripData(); }, [tripId]);

  const fetchTripData = async () => {
    try {
      const [tripRes, expensesRes] = await Promise.all([
        axios.get(`${API}/trips/${tripId}`),
        axios.get(`${API}/expenses`, { params: { trip_id: tripId } })
      ]);
      setTrip(tripRes.data);
      setExpenses(expensesRes.data);
    } catch (err) { console.error('Error fetching trip data:', err); }
    finally { setLoading(false); }
  };

  const handleStatusChange = async (newStatus) => {
    setActionLoading(true);
    try {
      await axios.patch(`${API}/trips/${tripId}/status`, { status: newStatus, remarks: statusRemarks });
      await fetchTripData();
      setShowStatusDropdown(false);
      setStatusRemarks('');
    } catch (err) { alert(err.response?.data?.detail || 'Failed to update status'); }
    finally { setActionLoading(false); }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setNoteLoading(true);
    try {
      await axios.post(`${API}/trips/${tripId}/notes`, { text: noteText });
      setNoteText('');
      await fetchTripData();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to add note'); }
    finally { setNoteLoading(false); }
  };

  const isHROrAbove = ['admin', 'management', 'hr', 'dgm'].includes(user?.role);
  const isHROrAdmin = ['admin', 'hr'].includes(user?.role);
  const isAccountsOrAdmin = ['admin', 'accounts'].includes(user?.role);
  const canManage = user?.role !== 'bdm' || trip?.employee_id === user?.id;
  const isDomesticTrip = trip?.trip_type === 'Domestic';

  const getAvailableActions = () => {
    if (!trip) return [];
    const actions = [];
    const s = trip.status;
    if (s === 'ongoing' && (user?.role === 'bdm' && trip.employee_id === user?.id || isHROrAbove))
      actions.push({ status: 'submitted', label: 'Submit for Review', color: 'bg-amber-600 hover:bg-amber-700' });
    if (s === 'submitted') {
      // Domestic: DGM/Management/HR/Admin can approve
      // International: Accounts/Admin can approve
      const canApprove = isDomesticTrip ? isHROrAbove : isAccountsOrAdmin;
      if (canApprove) {
        actions.push({ status: 'approved', label: 'Approve Trip', color: 'bg-emerald-600 hover:bg-emerald-700' });
        actions.push({ status: 'ongoing', label: 'Send Back', color: 'bg-gray-600 hover:bg-gray-700' });
      }
    }
    if (s === 'approved') {
      // Domestic: HR/Admin settle; International: Accounts/Admin settle
      const canSettle = isDomesticTrip ? isHROrAdmin : isAccountsOrAdmin;
      if (canSettle)
        actions.push({ status: 'settled', label: 'Close / Settle Trip', color: 'bg-purple-600 hover:bg-purple-700' });
    }
    return actions;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="text-gray-500">Loading...</div></div>;
  if (!trip) return <div className="p-6"><div className="card text-center py-12"><p className="text-gray-500">Trip not found</p></div></div>;

  const tripCur = trip?.currency || 'INR';
  const balance = trip.advance_amount - trip.total_expenses;
  const actions = getAvailableActions();

  return (
    <div className="p-4 md:p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <button onClick={() => navigate('/trips')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors self-start" data-testid="back-to-trips">
          <ArrowLeft className="w-5 h-5 text-gray-500" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h1 className="text-lg md:text-xl font-bold text-gray-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{trip.trip_name}</h1>
            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${STATUS_STYLES[trip.status]}`}>{trip.status.toUpperCase()}</span>
          </div>
          <p className="text-sm text-gray-500">{trip.employee_name} — {trip.trip_type} ({trip.currency || 'INR'})</p>
        </div>
        {actions.length > 0 && (
          <div className="relative self-start sm:self-center">
            <button onClick={() => setShowStatusDropdown(!showStatusDropdown)} className="btn btn-primary" data-testid="trip-actions-btn">
              Actions <ChevronDown className="w-4 h-4" />
            </button>
            {showStatusDropdown && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 p-3 z-20" data-testid="trip-actions-dropdown">
                <textarea value={statusRemarks} onChange={(e) => setStatusRemarks(e.target.value)} placeholder="Add remarks (optional)" className="input-field h-16 resize-none text-xs mb-2" data-testid="status-remarks-input" />
                {actions.map(action => (
                  <button key={action.status} onClick={() => handleStatusChange(action.status)} disabled={actionLoading}
                    className={`w-full px-3 py-2 rounded-lg text-sm text-white font-medium ${action.color} transition-colors mb-1`} data-testid={`action-${action.status}`}>
                    {actionLoading ? 'Processing...' : action.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        <ExportButton type="trip-detail" fixedFilters={{ trip_id: tripId }} label="Export" size="sm" />
      </div>

      {/* Reconciliation Cards (Section 5) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
        <div className="stat-card">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <span className="text-[10px] text-gray-400 uppercase tracking-wider">Duration</span>
          </div>
          <div className="text-sm font-semibold text-gray-900">{trip.start_date}</div>
          <div className="text-xs text-gray-500">to {trip.end_date}</div>
        </div>
        <div className="stat-card border-l-4 border-l-blue-500">
          <div className="text-[10px] text-gray-400 uppercase tracking-wider mb-2">Advance Given</div>
          <div className="text-base md:text-lg font-bold text-gray-900">{fmtCur(trip.advance_amount, tripCur)}</div>
        </div>
        <div className="stat-card border-l-4 border-l-indigo-500">
          <div className="text-[10px] text-gray-400 uppercase tracking-wider mb-2">Total Spent</div>
          <div className="text-base md:text-lg font-bold text-gray-900">{fmtCur(trip.total_expenses, tripCur)}</div>
          <div className="text-xs text-gray-500">{trip.expense_count} expense(s)</div>
        </div>
        <div className={`stat-card border-l-4 ${balance >= 0 ? 'border-l-emerald-500' : 'border-l-red-500'}`}>
          <div className="text-[10px] text-gray-400 uppercase tracking-wider mb-2">
            {balance >= 0 ? 'Balance (Refund Due)' : 'Overused'}
          </div>
          <div className={`text-base md:text-lg font-bold flex items-center gap-1 ${balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {balance < 0 && <AlertTriangle className="w-4 h-4" />}
            {fmtCur(Math.abs(balance), tripCur)}
          </div>
          {trip.settlement_amount !== null && <div className="text-xs text-gray-500">Settlement: {fmtCur(trip.settlement_amount, tripCur)}</div>}
        </div>
      </div>

      {trip.description && (
        <div className="card mb-4">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Description</h3>
          <p className="text-sm text-gray-700">{trip.description}</p>
        </div>
      )}

      {/* Expenses */}
      <div className="card mb-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Trip Expenses</h3>
          {trip.status === 'ongoing' && canManage && (
            <button onClick={() => navigate(`/expenses/new?trip_id=${trip.id}&trip_name=${encodeURIComponent(trip.trip_name)}`)}
              className="btn btn-primary text-xs py-1.5 px-3" data-testid="add-expense-to-trip">
              <Plus className="w-3 h-3" /> Add Expense
            </button>
          )}
        </div>
        {expenses.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" strokeWidth={1.5} />
            <p className="text-sm text-gray-500">No expenses added yet</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Date</th><th>Category</th><th>Vendor</th><th>Amount</th><th>Payment</th><th>Status</th></tr></thead>
              <tbody>
                {expenses.map(exp => (
                  <tr key={exp.id} data-testid={`trip-expense-${exp.id}`}>
                    <td>{new Date(exp.expense_date).toLocaleDateString()}</td>
                    <td>{exp.category}</td>
                    <td>
                      {exp.vendor}
                      {exp.duplicate_warning && <span className="ml-1 text-[10px] px-1 py-0.5 bg-amber-100 text-amber-700 rounded">DUP</span>}
                    </td>
                    <td className="font-semibold">{fmtCur(exp.amount, exp.currency || tripCur)}</td>
                    <td><span className="text-xs font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-700">{exp.payment_mode}</span></td>
                    <td><span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${EXPENSE_STATUS_STYLES[exp.approval_status] || ''}`}>{exp.approval_status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {expenses.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-100 flex justify-end">
            <div className="text-sm"><span className="text-gray-500">Total: </span><span className="font-bold text-gray-900">{fmtCur(expenses.reduce((s, e) => s + e.amount, 0), tripCur)}</span></div>
          </div>
        )}
      </div>

      {/* Notes Section (Section 12) */}
      <div className="card" data-testid="trip-notes-section">
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare className="w-4 h-4 text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Notes & Comments</h3>
        </div>

        {(trip.notes || []).length > 0 ? (
          <div className="space-y-3 mb-4">
            {trip.notes.map((note, i) => (
              <div key={i} className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold text-gray-700">{note.by}</span>
                  <span className="text-[10px] text-gray-400 uppercase">{note.role}</span>
                  <span className="text-[10px] text-gray-400">{new Date(note.at).toLocaleString()}</span>
                </div>
                <p className="text-sm text-gray-600">{note.text}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400 mb-4">No notes yet</p>
        )}

        {isHROrAbove && (
          <div className="flex gap-2">
            <input value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a note..."
              className="input-field flex-1" data-testid="trip-note-input"
              onKeyDown={(e) => e.key === 'Enter' && handleAddNote()} />
            <button onClick={handleAddNote} disabled={noteLoading || !noteText.trim()} className="btn btn-primary px-3" data-testid="add-trip-note-btn">
              <Send className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TripDetail;
